import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import {
  Map as MapIcon,
  Leaf,
  HeartHandshake,
  ShieldAlert,
  TreePine,
  Droplets,
  Users,
  GraduationCap,
  Sparkles,
  Building2,
} from "lucide-react";
import { PageHeader, KpiCard, SectionCard, RagBadge } from "@/components/Shared";
import MiningMap from "@/components/MiningMap";
import ReadinessScore from "@/components/ReadinessScore";
import {
  miningSites,
  rehabilitation,
  rehabProgressTrend,
  dmreCompliance,
  slpProjects,
  employmentEquity,
  smeParticipation,
  esgScores,
  esgRisks,
} from "@/data/geoEsgData";

const tooltipStyle = {
  contentStyle: { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 },
};

const severityStyles = {
  green: "border-emerald-200 bg-emerald-50/40",
  amber: "border-amber-200 bg-amber-50/40",
  red:   "border-red-200 bg-red-50/40",
};

const projectStatusStyles = {
  Active:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Completed:     "bg-blue-50 text-blue-700 border-blue-200",
};

export default function GeoEsgIntelligence() {
  const [selectedSite, setSelectedSite] = useState(miningSites[0]);

  const radarData = [
    { axis: "Environmental", score: esgScores.environmental, benchmark: 65 },
    { axis: "Social",        score: esgScores.social,        benchmark: 70 },
    { axis: "Governance",    score: esgScores.governance,    benchmark: 76 },
  ];

  return (
    <div data-testid="page-geo-esg">
      <PageHeader
        eyebrow="Geological, ESG & Rehabilitation Intelligence"
        title="From orebody to closure — one strategic view."
        subtitle="Reserves, environmental obligations, social commitments and ESG risk — connected for investors, operators and regulators."
      />

      {/* Mine Sustainability & Readiness Score */}
      <div className="mb-6">
        <ReadinessScore />
      </div>

      {/* SECTION 1 — GEOLOGICAL & RESERVE MAPPING */}
      <SectionCard
        title="Geological & Reserve Mapping"
        description="Interactive GIS view — site boundaries, drill points, reserve heat zones across 4 commodities"
        action={<MapIcon size={16} className="text-amber-700" />}
        className="mb-6"
      >
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 h-[520px]" data-testid="mining-map">
            <MiningMap onSelectSite={setSelectedSite} />
          </div>

          <div className="space-y-3" data-testid="site-detail-panel">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Selected site</div>
            <h3 className="font-display text-lg font-semibold text-slate-900" data-testid="selected-site-name">
              {selectedSite.name}
            </h3>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold uppercase tracking-wider">
                {selectedSite.region}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-bold uppercase tracking-wider">
                {selectedSite.status}
              </span>
            </div>

            <dl className="text-sm space-y-2 mt-3 pt-3 border-t border-slate-100">
              <Row label="Commodity" value={selectedSite.commodity} />
              <Row label="Reserves"  value={selectedSite.reserves} />
              <Row label="Grade"     value={selectedSite.grade} />
              <Row label="Active area" value={selectedSite.activeAreaHa > 0 ? `${selectedSite.activeAreaHa} ha` : "Pre-development"} />
              <Row label="Drill holes" value={`${selectedSite.drillPoints.length} points`} />
            </dl>

            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Other tenements</div>
              <div className="flex flex-wrap gap-1.5">
                {miningSites.filter((s) => s.id !== selectedSite.id).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSite(s)}
                    data-testid={`site-chip-${s.id}`}
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-slate-200 bg-white text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-colors"
                  >
                    {s.region}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 text-[11px] text-slate-500 italic">
              Map basemap: OpenTopoMap. Polygons illustrative for due-diligence visualisation. 3D terrain (CesiumJS / Mapbox 3D) ready for future integration.
            </div>
          </div>
        </div>
      </SectionCard>

      {/* SECTION 2 — ESG & REHABILITATION */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="rehab-disturbed" label="Disturbed Land" value={`${rehabilitation.disturbedHa.toLocaleString()} ha`} unit="cumulative footprint" />
        <KpiCard testid="rehab-rehabilitated" label="Rehabilitated" value={`${rehabilitation.rehabilitatedHa.toLocaleString()} ha`} unit={`${rehabilitation.progressPct}% of disturbed`} accent="green" />
        <KpiCard testid="rehab-outstanding" label="Outstanding" value={`${rehabilitation.outstandingHa.toLocaleString()} ha`} unit="to be rehabilitated" accent="amber" />
        <KpiCard testid="rehab-closure" label="Closure Readiness" value={`${rehabilitation.closureReadinessPct}%`} unit="DMRE-aligned" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard
          title="Rehabilitation Progress"
          description="Hectares rehabilitated · cumulative target trend"
          action={<TreePine size={16} className="text-emerald-700" />}
          className="xl:col-span-2"
        >
          <div className="h-72" data-testid="rehab-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rehabProgressTrend} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="rehab" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="year" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}ha`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `${v} ha`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="rehabilitatedHa" stroke="#059669" fill="url(#rehab)" strokeWidth={2.5} name="Rehabilitated (ha)" />
                <Area type="monotone" dataKey="target" stroke="#B45309" fill="none" strokeWidth={2} strokeDasharray="4 4" name="Target (ha)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Environmental Provisions" description="ZAR millions held / committed">
          <div className="space-y-3" data-testid="env-provisions">
            <ProvisionRow label="Closure liability provision" value={`R${rehabilitation.liabilityProvisionRm}m`} sub="Standard Bank escrow" tone="slate" />
            <ProvisionRow label="Rehabilitation guarantee"    value={`R${rehabilitation.rehabGuaranteeRm}m`} sub="In favour of DMRE" tone="emerald" />
            <ProvisionRow label="Water-quality provision"     value={`R${rehabilitation.waterComplianceProvisionRm}m`} sub="Pending DWS Q2 audit" tone="amber" />
            <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-900 text-sm">Total ESG / closure cover</span>
              <span className="font-display text-lg font-bold text-slate-900">
                R{(rehabilitation.liabilityProvisionRm + rehabilitation.rehabGuaranteeRm + rehabilitation.waterComplianceProvisionRm)}m
              </span>
            </div>
            <div className="text-[11px] text-slate-500">
              Aligned with closure cost modelled in <span className="text-amber-700 font-semibold">Financial &amp; Value Intelligence</span>.
            </div>
          </div>
        </SectionCard>
      </div>

      {/* DMRE compliance */}
      <SectionCard
        title="DMRE Compliance & Closure Plan"
        description="Statutory rehabilitation and closure register"
        action={<ShieldAlert size={16} className="text-amber-700" />}
        className="mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dmre-compliance">
          {dmreCompliance.map((c) => (
            <div key={c.item} className={`border rounded-md p-3 ${severityStyles[c.status]}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-semibold text-slate-900">{c.item}</span>
                <RagBadge level={c.status} />
              </div>
              <p className="text-xs text-slate-700">{c.note}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* SECTION 3 — SLP TRACKING */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard
          title="Social & Labour Plan Projects"
          description="LED, community, skills and enterprise development"
          action={<HeartHandshake size={16} className="text-amber-700" />}
          className="xl:col-span-2"
        >
          <div className="overflow-x-auto" data-testid="slp-projects-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-4 font-semibold">Project</th>
                  <th className="py-3 pr-4 font-semibold">Category</th>
                  <th className="py-3 pr-4 font-semibold text-right">Budget</th>
                  <th className="py-3 pr-4 font-semibold w-44">Progress</th>
                  <th className="py-3 pr-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {slpProjects.map((p) => (
                  <tr key={p.name} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`slp-${p.name.split(" ")[0].toLowerCase()}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900">{p.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{p.beneficiaries}</div>
                    </td>
                    <td className="py-3 pr-4 text-slate-700 text-xs">{p.category}</td>
                    <td className="py-3 pr-4 text-right font-display font-semibold text-slate-900">R{p.budgetRm}m</td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-600" style={{ width: `${p.completionPct}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-700 w-9 text-right">{p.completionPct}%</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${projectStatusStyles[p.status]}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <SectionCard title="Employment Equity" description="Actuals vs SLP targets" action={<Users size={16} className="text-amber-700" />}>
          <div className="space-y-3" data-testid="employment-equity">
            {employmentEquity.map((e) => (
              <div key={e.metric}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-700 truncate pr-2">{e.metric}</span>
                  <span className="font-semibold text-slate-900 tabular-nums">
                    {e.actualPct}% <span className="text-[10px] text-slate-500">/ {e.targetPct}%</span>
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${e.status === "green" ? "bg-emerald-600" : e.status === "amber" ? "bg-amber-600" : "bg-red-600"}`}
                    style={{ width: `${(e.actualPct / Math.max(e.targetPct, e.actualPct)) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="sme-stats">
        <SmeStat icon={Building2} label="Local procurement" value={`${smeParticipation.localProcurementPct}%`} sub="of total spend" />
        <SmeStat icon={Sparkles} label="BBBEE-compliant suppliers" value={smeParticipation.bbbeeCompliantSuppliers} sub="active vendors" />
        <SmeStat icon={GraduationCap} label="SMEs incubated (24mo)" value={smeParticipation.smesIncubated24mo} sub="local enterprises" />
        <SmeStat icon={Droplets} label="Total SME spend" value={`R${smeParticipation.totalSpendOnSmesRm}m`} sub="2024" />
      </div>

      {/* SECTION 4 — ESG & INVESTMENT RISK */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard
          title="ESG Score Profile"
          description={esgScores.benchmark}
          action={<Leaf size={16} className="text-emerald-700" />}
        >
          <div className="h-72" data-testid="esg-radar">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="axis" tick={{ fill: "#475569", fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 10 }} stroke="#cbd5e1" />
                <Radar name="Tzaneen" dataKey="score" stroke="#0F172A" fill="#0F172A" fillOpacity={0.5} />
                <Radar name="Industry median" dataKey="benchmark" stroke="#B45309" fill="#B45309" fillOpacity={0.15} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip {...tooltipStyle} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Composite</div>
              <div className="font-display text-2xl font-bold text-slate-900" data-testid="esg-composite">{esgScores.composite}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">E / S / G</div>
              <div className="font-display text-base font-semibold text-slate-700 mt-1">
                {esgScores.environmental} · {esgScores.social} · {esgScores.governance}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vs median</div>
              <div className="font-display text-base font-semibold text-emerald-700 mt-1">+8.0</div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="ESG & Compliance Risk Areas"
          description="Outstanding obligations and investor concerns"
          action={<ShieldAlert size={16} className="text-red-600" />}
          className="xl:col-span-2"
        >
          <div className="space-y-2.5" data-testid="esg-risks">
            {esgRisks.map((r) => (
              <div key={r.area} className={`border rounded-md p-3 flex items-start justify-between gap-3 ${severityStyles[r.severity]}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{r.area}</div>
                  <p className="text-xs text-slate-700 mt-0.5">{r.note}</p>
                </div>
                <RagBadge level={r.severity} />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
      <dd className="text-sm text-slate-900 font-medium text-right">{value}</dd>
    </div>
  );
}

function ProvisionRow({ label, value, sub, tone }) {
  return (
    <div className={`border-l-2 pl-3 py-1 border-${tone}-600`} style={{ borderLeftColor: tone === "emerald" ? "#059669" : tone === "amber" ? "#D97706" : "#475569" }}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700">{label}</span>
        <span className="font-display font-bold text-slate-900">{value}</span>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function SmeStat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-amber-700" />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
